import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { TID, LOGO_URL, WORDMARK_URL } from "../../lib/testIds";
import { useTheme } from "../../hooks/useTheme";

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const navItems = [
    { label: "Product", target: "what-is" },
    { label: "Solution", target: "solution" },
    { label: "Vision", target: "vision" },
];

const ThemeToggle = () => {
    const { theme, toggleTheme } = useTheme();
    const btnRef = useRef(null);

    const handleToggle = async () => {
        // Fallback for browsers without View Transitions API
        if (!document.startViewTransition) {
            toggleTheme();
            return;
        }

        const btn = btnRef.current;
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        // Radius large enough to cover the farthest corner of the screen
        const maxRadius = Math.hypot(
            Math.max(cx, window.innerWidth  - cx),
            Math.max(cy, window.innerHeight - cy)
        );

        const transition = document.startViewTransition(() => {
            // flushSync ensures React commits the state + DOM update
            // synchronously so the VT snapshot captures the new theme
            flushSync(() => toggleTheme());
        });

        await transition.ready;

        // Animate the new snapshot expanding as a circle from the button
        document.documentElement.animate(
            {
                clipPath: [
                    `circle(0px at ${cx}px ${cy}px)`,
                    `circle(${maxRadius}px at ${cx}px ${cy}px)`,
                ],
            },
            {
                duration: 520,
                easing: "ease-in-out",
                pseudoElement: "::view-transition-new(root)",
            }
        );
    };

    return (
        <motion.button
            ref={btnRef}
            onClick={handleToggle}
            data-testid="theme-toggle"
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            className="relative flex items-center justify-center w-9 h-9 rounded-full border border-black/[0.08] bg-white/80 dark:border-white/10 dark:bg-white/[0.06] backdrop-blur-sm transition-colors hover:border-violet-300 dark:hover:border-brand-purple/40 hover:bg-violet-50 dark:hover:bg-brand-purple/10"
        >
            <AnimatePresence mode="wait" initial={false}>
                {theme === "dark" ? (
                    <motion.span
                        key="sun"
                        initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="absolute"
                    >
                        <Sun size={16} className="text-white/70" />
                    </motion.span>
                ) : (
                    <motion.span
                        key="moon"
                        initial={{ opacity: 0, rotate: 90, scale: 0.6 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        exit={{ opacity: 0, rotate: -90, scale: 0.6 }}
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="absolute"
                    >
                        <Moon size={16} className="text-[#6B7280]" />
                    </motion.span>
                )}
            </AnimatePresence>
        </motion.button>
    );
};

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const { theme } = useTheme();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="fixed top-0 left-0 right-0 z-50"
        >
            <div className="px-4 sm:px-6 pt-4">
                <div
                    className={`mx-auto max-w-6xl flex items-center justify-between rounded-full border transition-all duration-500 ${
                        scrolled
                            ? theme === "dark"
                                ? "border-white/10 bg-[#06070F]/75 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
                                : "border-black/[0.07] bg-white/88 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.07)]"
                            : "border-transparent bg-transparent"
                    } px-4 py-2.5`}
                >
                    <button
                        onClick={() => scrollTo("hero")}
                        data-testid={TID.navLogo}
                        className="flex items-center gap-2.5 group"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Lambda Quest"
                            className="w-9 h-9 object-contain"
                            style={{
                                filter:
                                    theme === "dark"
                                        ? "drop-shadow(0 0 8px rgba(157,76,221,0.4))"
                                        : "drop-shadow(0 0 8px rgba(139,92,246,0.3))",
                            }}
                        />
                        <div className="hidden sm:flex relative overflow-hidden h-4 items-center">
                            <img
                                src={WORDMARK_URL}
                                alt="LAMBDA QUEST"
                                className="h-[90px] w-auto max-w-none"
                            />
                        </div>
                    </button>

                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map((n) => (
                            <button
                                key={n.label}
                                onClick={() => scrollTo(n.target)}
                                className="px-4 py-2 rounded-full text-sm text-[#6B7280] hover:text-[#111827] hover:bg-black/[0.04] dark:text-white/65 dark:hover:text-white dark:hover:bg-white/[0.05] transition-colors duration-200"
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2.5">
                        <ThemeToggle />
                        <button
                            onClick={() => scrollTo("waitlist")}
                            data-testid={TID.navJoin}
                            className="btn-glow inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2 transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]
                            bg-gradient-to-r from-violet-500 via-purple-500 to-cyan-400 text-white shadow-[0_4px_16px_rgba(139,92,246,0.35)]
                            dark:bg-none dark:bg-white dark:text-black dark:shadow-none dark:[background-image:none]"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </div>
        </motion.header>
    );
};

export default Nav;
